import { useEffect } from "react";
import type { QueryClient } from "@tanstack/react-query";
import { QueryClientProvider } from "@tanstack/react-query";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "@/i18n/LanguageContext";
import { InstallPrompt } from "@/components/InstallPrompt";
import { CookieConsent } from "@/components/CookieConsent";
import NotFound from "@/pages/NotFound";
import { trackPageView } from "@/lib/analytics";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { installGlobalErrorHandlers } from "@/lib/error-logger";
import appCss from "../styles.css?url";

// ported from main.tsx (SSR-safe: guards on typeof window internally)
installGlobalErrorHandlers();

const SITE_TITLE = "De Hussel App – Automatische hussel-indelingen voor tennisclubs";
const SITE_DESCRIPTION =
  "Dé app voor het organiseren van husselavonden bij tennisclubs. Automatische baanindeling op basis van speelniveau. Geen vrijwilligers nodig. Probeer het nu!";
const SOCIAL_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/O12myWaJT9YvgEsvLHw4YrAWwGf1/social-images/social-1766583653843-hussel-app-social.png";

const JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      name: "De Hussel App",
      url: "https://dehusselapp.nl",
      logo: "https://dehusselapp.nl/hussel-app-icon-2027-512.png",
      description:
        "Dé efficiënte en gebruiksvriendelijke tennis app voor geautomatiseerde hussel-indelingen bij tennisclubs.",
      sameAs: [],
    },
    {
      "@type": "WebSite",
      name: "De Hussel App",
      url: "https://dehusselapp.nl",
      potentialAction: {
        "@type": "SearchAction",
        target: "https://dehusselapp.nl/?q={search_term_string}",
        "query-input": "required name=search_term_string",
      },
    },
    {
      "@type": "SoftwareApplication",
      name: "De Hussel App",
      applicationCategory: "SportsApplication",
      operatingSystem: "Web",
      url: "https://dehusselapp.nl",
      description:
        "Automatische hussel-indelingen voor tennisclubs. Spelers melden zich aan via hun telefoon en krijgen direct hun baanindeling.",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "EUR",
        description: "Gratis proefperiode beschikbaar",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.6",
        reviewCount: "10",
        bestRating: "5",
      },
    },
  ],
});

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      {
        name: "viewport",
        content: "width=device-width, initial-scale=1.0, viewport-fit=cover",
      },
      { title: SITE_TITLE },
      { name: "description", content: SITE_DESCRIPTION },
      { name: "author", content: "Markant Tennis" },
      {
        name: "google-site-verification",
        content: "hY4qYFEyWrpXFn4hXWwGwy09_aejwLoKQbZbXSiSVKE",
      },
      // PWA meta
      { name: "theme-color", content: "#ffffff" },
      { name: "apple-mobile-web-app-capable", content: "yes" },
      { name: "apple-mobile-web-app-status-bar-style", content: "black-translucent" },
      { name: "apple-mobile-web-app-title", content: "De Hussel App" },
      { name: "mobile-web-app-capable", content: "yes" },
      { name: "format-detection", content: "telephone=no" },
      // Open Graph / Twitter
      { property: "og:type", content: "website" },
      { property: "og:title", content: SITE_TITLE },
      { property: "og:description", content: SITE_DESCRIPTION.replace(" Probeer het nu!", "") },
      { property: "og:image", content: SOCIAL_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:site", content: "@lovable_dev" },
      { name: "twitter:title", content: SITE_TITLE },
      { name: "twitter:description", content: SITE_DESCRIPTION.replace(" Probeer het nu!", "") },
      { name: "twitter:image", content: SOCIAL_IMAGE },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "icon", type: "image/svg+xml", href: "/favicon.svg" },
      { rel: "apple-touch-icon", sizes: "192x192", href: "/hussel-app-icon-2027-192.png" },
      { rel: "apple-touch-icon", sizes: "512x512", href: "/hussel-app-icon-2027-512.png" },
      { rel: "manifest", href: "/manifest.webmanifest" },
      { rel: "preload", href: "/metal-background-optimized.webp", as: "image", fetchPriority: "high" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      { rel: "preconnect", href: "https://www.mollie.com" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@400;500;600;700&family=Righteous&display=swap",
      },
    ],
    scripts: [
      // Google Consent Mode v2 defaults — ported from index.html
      {
        children: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('consent', 'default', {
  ad_storage: 'granted',
  analytics_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  personalization_storage: 'granted',
  functionality_storage: 'granted',
  security_storage: 'granted',
  wait_for_update: 500
});`,
      },
      // Google Tag Manager — ported from index.html
      {
        children: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','GTM-PSK9TGJK');`,
      },
      // Google tag (gtag.js) — ported from index.html
      { src: "https://www.googletagmanager.com/gtag/js?id=G-GSN739DLXQ", async: true },
      {
        children: `gtag('js', new Date());
gtag('config', 'G-GSN739DLXQ', {
  cookie_domain: 'dehusselapp.nl',
  cookie_flags: 'SameSite=None;Secure',
  linker: {
    domains: ['dehusselapp.nl', 'dehusselapp.lovable.app'],
    accept_incoming: true
  }
});`,
      },
      // Theme bootstrap (before first paint) — ported from index.html body script
      {
        children: `(function(){
try{
  if(!localStorage.getItem('app-theme-default-light')){
    localStorage.setItem('app-theme','light');
    localStorage.setItem('app-theme-default-light','1');
  }
  var t=localStorage.getItem('app-theme');
  if(t==='dark'){document.body&&document.body.classList.add('dark');document.documentElement.classList.add('dark')}
}catch(e){}
})();`,
      },
      // JSON-LD structured data — ported from index.html
      { type: "application/ld+json", children: JSON_LD },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="nl" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body suppressHydrationWarning>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  // Hydration marker — lets E2E tests wait until React is interactive
  // instead of typing into server-rendered (inert) HTML.
  useEffect(() => {
    document.documentElement.setAttribute("data-hydrated", "true");
  }, []);

  // Register the push service worker (client-only, after hydration).
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .catch((error) => console.error("[SW] registration failed:", error));
  }, []);

  // Single source of pageview tracking (replaces the old per-page SEOHead).
  useEffect(() => {
    trackPageView(pathname, typeof document !== "undefined" ? document.title : undefined);
  }, [pathname]);

  return (

    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <InstallPrompt />
          <CookieConsent />
          <Outlet />
        </TooltipProvider>
      </LanguageProvider>
    </QueryClientProvider>
  );
}

function ErrorComponent({ error, reset }: ErrorComponentProps) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-foreground px-4">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-bold">Deze pagina kon niet laden</h1>
        <p className="text-muted-foreground">
          Er ging iets mis bij het laden van deze pagina. Probeer het opnieuw of ga
          terug naar de homepage.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Probeer opnieuw
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted"
          >
            Naar home
          </a>
        </div>
      </div>
    </div>
  );
}
